import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../shared/api';
import { adminSectionDefinition, adminSectionNavLabel } from '../shared/adminSectionRegistry';

/**
 * m.6 faza 3 (Vedran 24.09.2026): mapping partnera po tvrtki, URA i IRA zasebno: sektor, lvl1-5.
 * Partneri dolaze iz računa; država i P/F iz Farosovog šifrarnika (bi_partner_profiles).
 */
type Lvl = 'lvl1' | 'lvl2' | 'lvl3' | 'lvl4' | 'lvl5';
const LVLS: Lvl[] = ['lvl1', 'lvl2', 'lvl3', 'lvl4', 'lvl5'];

interface MapRow {
    partner_code: string; name: string | null; oib: string | null; country_code: string | null; region: 'hr' | 'eu' | 'non_eu' | null;
    is_person: boolean | null; invoices: number; amount: number; sector_id: number | null;
    lvl1: string | null; lvl2: string | null; lvl3: string | null; lvl4: string | null; lvl5: string | null;
}
interface Payload {
    rows: MapRow[]; total: number; page: number; per_page: number;
    sectors: { id: number; code: string; name: string }[];
    suggestions: Record<Lvl, string[]>;
    companies: { id: number; code: string; name: string }[];
}

export function AdminPartnerMappingSection({ token, locale }: { token: string; locale: 'hr' | 'en' }) {
    const def = adminSectionDefinition('113');
    const title = def ? adminSectionNavLabel(def, locale) : '113';
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [companyId, setCompanyId] = useState('1');
    const [direction, setDirection] = useState<'ura' | 'ira'>('ura');
    const [search, setSearch] = useState('');
    const [unmapped, setUnmapped] = useState(false);
    const [b2, setB2] = useState('');
    const [region, setRegion] = useState('');
    const [page, setPage] = useState(1);
    const [data, setData] = useState<Payload | null>(null);
    const [dirty, setDirty] = useState<Record<string, MapRow>>({});
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState('');

    const load = useCallback(async () => {
        setLoading(true); setMsg('');
        try {
            const p = new URLSearchParams({ company_id: companyId, direction, page: String(page) });
            if (search.trim()) p.set('q', search.trim());
            if (unmapped) p.set('unmapped', '1');
            if (b2) p.set('b2', b2);
            if (region) p.set('region', region);
            setData(await api<Payload>(`/admin/partner-mappings?${p.toString()}`, token));
            setDirty({});
        } catch (err) { setMsg(err instanceof Error ? err.message : 'API'); } finally { setLoading(false); }
    }, [token, companyId, direction, page, search, unmapped, b2, region]);
    useEffect(() => { void load(); }, [load]);
    useEffect(() => { setPage(1); }, [companyId, direction, search, unmapped, b2, region]);

    const edit = (r: MapRow, patch: Partial<MapRow>) => setDirty((d) => ({ ...d, [r.partner_code]: { ...(d[r.partner_code] ?? r), ...patch } }));

    const save = async () => {
        const rows = Object.values(dirty).map((r) => ({ partner_code: r.partner_code, sector_id: r.sector_id, lvl1: r.lvl1, lvl2: r.lvl2, lvl3: r.lvl3, lvl4: r.lvl4, lvl5: r.lvl5 }));
        if (rows.length === 0) return;
        try {
            const res = await api<{ saved: number; cleared: number }>('/admin/partner-mappings', token, {
                method: 'POST', body: JSON.stringify({ company_id: Number(companyId), direction, rows }),
            });
            setMsg(t(`Spremljeno: ${res.saved}, obrisano: ${res.cleared}.`, `Saved: ${res.saved}, cleared: ${res.cleared}.`));
            await load();
        } catch (err) { setMsg(err instanceof Error ? err.message : 'API'); }
    };

    const [aiBusy, setAiBusy] = useState(false);
    const suggest = async () => {
        if (!data) return;
        const codes = data.rows.filter((r) => r.is_person !== true && !r.lvl1 && !r.sector_id).map((r) => r.partner_code).slice(0, 50);
        if (codes.length === 0) { setMsg(t('Na stranici nema pravnih osoba bez mappinga.', 'No unmapped legal entities on this page.')); return; }
        setAiBusy(true); setMsg('');
        try {
            const res = await api<{ suggestions: Record<string, Partial<MapRow>> }>('/admin/partner-mappings/suggest', token, {
                method: 'POST', body: JSON.stringify({ company_id: Number(companyId), direction, partner_codes: codes }),
            });
            const byCode = Object.fromEntries(data.rows.map((r) => [r.partner_code, r]));
            setDirty((d) => {
                const next = { ...d };
                Object.entries(res.suggestions).forEach(([code, sug]) => { if (byCode[code]) next[code] = { ...(d[code] ?? byCode[code]), ...sug }; });
                return next;
            });
            setMsg(t(`AI prijedlog za ${Object.keys(res.suggestions).length} partnera. Pregledaj pa Spremi.`, `AI suggested ${Object.keys(res.suggestions).length} partners. Review, then Save.`));
        } catch (err) { setMsg(err instanceof Error ? err.message : 'API'); } finally { setAiBusy(false); }
    };

    const fmt = (n: number) => new Intl.NumberFormat('hr-HR', { maximumFractionDigits: 0 }).format(n);
    const regionLabel = (r: MapRow['region']) => r === 'hr' ? t('tuzemni', 'domestic') : r === 'eu' ? 'EU' : r === 'non_eu' ? t('izvan EU', 'non-EU') : '?';
    const pages = data ? Math.max(1, Math.ceil(data.total / data.per_page)) : 1;
    const dirtyCount = Object.keys(dirty).length;

    return (
        <section className="card">
            <header style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>{title}</h3>
                <span className="muted" style={{ fontSize: '0.84rem' }}>{t('Po tvrtki, URA i IRA zasebno: sektor i razine lvl1-5. Država i P/F iz Farosovog šifrarnika partnera.', 'Per company, AP and AR separately: sector and levels 1-5. Country and P/F from the Faros partner register.')}</span>
                <button type="button" disabled={aiBusy || !data} onClick={() => void suggest()} title={t('Prijedlog za pravne osobe bez mappinga na ovoj stranici (najviše 50). Ništa se ne sprema bez potvrde.', 'Suggest for unmapped legal entities on this page (max 50). Nothing is saved without confirmation.')} style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid #d8cdb2', borderRadius: 8, padding: '7px 14px', cursor: 'pointer' }}>
                    {aiBusy ? t('AI radi…', 'AI working…') : t('AI prijedlog', 'AI suggest')}
                </button>
                <button type="button" disabled={dirtyCount === 0} onClick={() => void save()} style={{ background: 'var(--ad-acc)', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', cursor: dirtyCount ? 'pointer' : 'default', fontWeight: 600, opacity: dirtyCount ? 1 : 0.5 }}>
                    {t('Spremi', 'Save')}{dirtyCount ? ` (${dirtyCount})` : ''}
                </button>
            </header>

            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} style={inp}>
                    {(data?.companies ?? [{ id: 1, code: 'ADO', name: 'ADO' }]).map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
                </select>
                <select value={direction} onChange={(e) => setDirection(e.target.value as 'ura' | 'ira')} style={inp}>
                    <option value="ura">URA</option>
                    <option value="ira">IRA</option>
                </select>
                <select value={region} onChange={(e) => setRegion(e.target.value)} style={inp}>
                    <option value="">{t('Sve regije', 'All regions')}</option>
                    <option value="hr">{t('Tuzemni', 'Domestic')}</option>
                    <option value="eu">EU</option>
                    <option value="non_eu">{t('Izvan EU', 'Non-EU')}</option>
                    <option value="unknown">{t('Nepoznato', 'Unknown')}</option>
                </select>
                <select value={b2} onChange={(e) => setB2(e.target.value)} style={inp}>
                    <option value="">B2B + B2C</option>
                    <option value="b2b">{t('B2B (pravne)', 'B2B (legal)')}</option>
                    <option value="b2c">{t('B2C (fizičke)', 'B2C (persons)')}</option>
                </select>
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('Traži šifru / naziv / OIB', 'Search code / name / OIB')} style={{ ...inp, minWidth: 240 }} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.84rem' }}>
                    <input type="checkbox" checked={unmapped} onChange={(e) => setUnmapped(e.target.checked)} /> {t('Samo bez mappinga', 'Unmapped only')}
                </label>
            </div>

            {msg ? <p className="muted" style={{ fontWeight: 600 }}>{msg}</p> : null}
            {loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}

            {!loading && data ? (
                <>
                    {LVLS.map((l) => (
                        <datalist key={l} id={`pm-${l}`}>{(data.suggestions[l] ?? []).map((v) => <option key={v} value={v} />)}</datalist>
                    ))}
                    <div className="table-wrap">
                        <table className="admin-user-table">
                            <thead>
                                <tr>
                                    <th>{t('Šifra', 'Code')}</th><th>{t('Naziv', 'Name')}</th><th>{t('Država', 'Country')}</th><th>P/F</th>
                                    <th style={{ textAlign: 'right' }}>{t('Računa', 'Invoices')}</th><th style={{ textAlign: 'right' }}>{t('Iznos', 'Amount')}</th>
                                    <th>{t('Sektor', 'Sector')}</th>{LVLS.map((l) => <th key={l}>{l}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {data.rows.length === 0 ? <tr><td colSpan={12} className="muted" style={{ padding: 14 }}>{t('Nema partnera.', 'No partners.')}</td></tr> : data.rows.map((r0) => {
                                    const r = dirty[r0.partner_code] ?? r0;
                                    return (
                                        <tr key={r.partner_code} style={{ background: dirty[r.partner_code] ? 'rgba(201,162,39,0.08)' : undefined }}>
                                            <td style={{ fontFamily: 'monospace' }}>{r.partner_code}</td>
                                            <td>{r.name ?? '—'}{r.oib ? <span className="muted"> · {r.oib}</span> : null}</td>
                                            <td title={r.country_code ?? ''}>{r.country_code ?? '?'} <span className="muted">{regionLabel(r.region)}</span></td>
                                            <td>{r.is_person == null ? '?' : r.is_person ? 'F' : 'P'}</td>
                                            <td style={{ textAlign: 'right' }}>{r.invoices}</td>
                                            <td style={{ textAlign: 'right' }}>{fmt(r.amount)}</td>
                                            <td>
                                                <select value={r.sector_id ?? ''} onChange={(e) => edit(r, { sector_id: e.target.value ? Number(e.target.value) : null })} style={{ ...inp, padding: '4px 6px' }}>
                                                    <option value="">—</option>
                                                    {data.sectors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                                </select>
                                            </td>
                                            {LVLS.map((l) => (
                                                <td key={l}>
                                                    <input list={`pm-${l}`} value={r[l] ?? ''} onChange={(e) => edit(r, { [l]: e.target.value } as Partial<MapRow>)} style={{ ...inp, padding: '4px 6px', width: 110 }} />
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, fontSize: '0.84rem' }}>
                        <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} style={inp}>‹</button>
                        <span>{page} / {pages} · {data.total} {t('partnera', 'partners')}</span>
                        <button type="button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} style={inp}>›</button>
                        {dirtyCount ? <span className="muted">{t('Promjene na stranici se gube pri promjeni stranice ili filtera ako se ne spreme.', 'Unsaved changes are lost when changing page or filter.')}</span> : null}
                    </div>
                </>
            ) : null}
        </section>
    );
}

const inp: React.CSSProperties = { padding: '7px 10px', borderRadius: 8, border: '1px solid #d8cdb2', fontSize: '0.86rem' };
